import React, { useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import LazyImage from '../../../hocs/Lazy/LazyImage';
import LogoReplace from '../../../hocs/LogoReplace/LogoReplace';
import { translate, prefix } from '../../../utils/locale';
import { imageSizeTranslation } from '../../../utils/image';
import { addEvent, removeEvent } from '../../../utils/dom';
import initVideo, { extractEmbedSource, destroyVideo, isVideoPaused, pauseVideo, playVideo } from '../../../utils/embed';
import { cleanInternalLinks } from '../../../utils/string';
import s from './Media.scss';
import * as Sentry from '@sentry/browser';

const VideoWrapper = ({ src, options = {}, sectionHeight, onInit = false, enableEvents = true, children }) => {
	const { autoplay = 0, loop = 0 } = options;
	const [showVideo, useVideo] = useState(false);
	const styles = {};
	if (sectionHeight) {
		styles[sectionHeight[0]] = sectionHeight[1];
	}
	const videoClick = (e) => {
		const $video = e.currentTarget.querySelector('video');
		if ($video) {
			if (enableEvents) {
				e.stopPropagation();
				e.preventDefault();

				if ($video.paused) {
					$video.play();
				} else {
					$video.pause();
				}
			}
		} else {
			// Update state to show video instead of children.
			useVideo(true);
		}
	};

	let innerNodes = <video className={[s.videoWrapper_video, (sectionHeight ? s.videoWrapper_video__fill : null)].join(' ')} src={src.url} autoPlay={autoplay ? 1 : null} loop={loop ? 1 : null} playsInline muted={autoplay ? true : null} style={styles} />;
	const videoClasses = [s.videoWrapper];
	if (!onInit) {
		if (showVideo) videoClasses.push(s.videoWrapper__live);
		else innerNodes = children;
	}

	if (!enableEvents) {
		return (
			<div className={videoClasses.join(' ')}>
				{ innerNodes }
			</div>
		);
	}

	return (
		<a className={videoClasses.join(' ')} href="#control-video-player" onClick={videoClick}>
			{ innerNodes }
		</a>
	);
};

const EmbedWrapper = ({ src, uid, img = null, options = {}, sectionHeight, settings = {}, onInit = false, enableEvents = true }) => {
	const [morePortrait, updateWindowAspectRatio] = useState(null);
	const [embedInitiated, initiateEmbed] = useState(false);
	const embedClasses = [s.embedWrapper];
	const embedStyles = {};
	const frameWidth = parseInt(settings.width, 10) || 560;
	const frameHeight = parseInt(settings.height, 10) || 315;
	const aspectRatio = frameHeight / frameWidth;

	const embedClick = (e) => {
		if (e.currentTarget.querySelector('iframe')) {
			if (enableEvents) {
				e.stopPropagation();
				e.preventDefault();

				isVideoPaused(src, uid).then((paused) => {
					if (paused) playVideo(src, uid);
					else pauseVideo(src, uid);

					return !paused;
				}).catch((err) => {
					Sentry.captureException(err);
					console.log(`DEBUG: unable to check if ${src} is paused`);
				});
			}
		} else {
			e.preventDefault();
			// Instantiate Embed.
			initVideo(src, uid, options, settings);
			initiateEmbed(true);
		}
	};

	useEffect(() => {
		let isMorePortrait = null;
		if (onInit) {
			initVideo(src, uid, options, settings);
			initiateEmbed(true);
		}

		const resizeEmbed = () => {
			if (sectionHeight[1]) {
				const windowAspectRatio = (window.innerHeight * (parseInt(sectionHeight[1], 10) / 100)) / window.innerWidth;

				if (windowAspectRatio > aspectRatio && (!isMorePortrait || isMorePortrait === null)) {
					updateWindowAspectRatio(true);
					isMorePortrait = true;
				} else if (windowAspectRatio < aspectRatio && (isMorePortrait || isMorePortrait === null)) {
					updateWindowAspectRatio(false);
					isMorePortrait = false;
				}
			}
		};

		addEvent(window, 'resize.embed', () => {
			window.requestAnimationFrame(resizeEmbed);
		});

		resizeEmbed();

		return () => {
			removeEvent(window, 'resize.embed');
			destroyVideo(src, uid);
		};
	});

	if (sectionHeight[1]) {
		if (morePortrait) {
			// more portrait
			embedStyles.height = `${parseInt(sectionHeight[1], 10)}vh`;
			embedStyles.width = `${parseInt(sectionHeight[1], 10) / aspectRatio}vh`;
		} else {
			// more landscape
			embedStyles.paddingBottom = `${aspectRatio * 100}%`;
		}
		embedClasses.push(s.embedWrapper__absolute);
		embedClasses.push(s[`embedWrapper__${settings?.origin?.horizontal}`]);
		embedClasses.push(s[`embedWrapper__${settings?.origin?.vertical}`]);
	} else if (onInit || (embedInitiated && !onInit)) {
		embedStyles.paddingBottom = `${aspectRatio * 100}%`;
	}

	/* Embed status classes */
	if (onInit) embedClasses.push(s.embedWrapper__auto);
	if (embedInitiated && !onInit) embedClasses.push(s.embedWrapper__live);

	const innerNodes = <div id={`iframe-${src}-${uid}`}>{ img }</div>;

	if (!enableEvents) {
		return (
			<div className={embedClasses.join(' ')} style={embedStyles}>
				{ innerNodes }
			</div>
		);
	}

	return (
		<a className={embedClasses.join(' ')} href="#control-video-embed" onClick={embedClick} style={embedStyles}>
			{ innerNodes }
		</a>
	);
};

const MediaCaptionLink = ({ content, setup, alreadyHasLink, linkNumber = '' }) => {
	const link = content[`link${linkNumber}`] ? translate(setup, content[`link${linkNumber}`], 'title') : null;

	if (link && !alreadyHasLink) {
		return <Link style={{ color: content.textDetails.color }} className={s.mediaCaption_link} to={prefix(setup, cleanInternalLinks(translate(setup, content[`link${linkNumber}`], 'url')))}>{ link }</Link>;
	}
	if (link) {
		return <span style={{ color: content.textDetails.color }} className={s.mediaCaption_link}><span>{ link }</span></span>;
	}
	return null;
};

const MediaCaption = ({ content, setup }) => {
	const HeaderTag = content.headerFormat.type;
	if (!content.header && !content.text && !content?.video?.source?.video) return null;
	const mediaCaptionClasses = [s.mediaCaption, s[`mediaCaption__${content.textDetails.origin.horizontal}`], s[`mediaCaption__${content.textDetails.origin.vertical}`]];

	return (
		<div className={mediaCaptionClasses.join(' ')} style={{ color: content.textDetails.color }}>
			<div className={s.mediaCaption_inner} style={{ textAlign: content.textDetails.align.horizontal }}>
				{
					content.header && (
						<HeaderTag className={[s.mediaCaption_headline, `u_fontSize__${content.headerFormat.size}`].join(' ')}>
							<LogoReplace str={translate(setup, content.header)} />
						</HeaderTag>
					)
				}
				{ content.header && content.text && <br /> }
				{ content.text && <span className={s.mediaCaption_text}>{ translate(setup, content.text) }</span> }
			</div>
		</div>
	);
};

const MediaContent = ({ content, columnWidth, uid, first = false, sectionHeight = false, alreadyHasLink = false, keepColumnsMobile = false }) => {
	const embedSrc = content?.video?.source?.embed && extractEmbedSource(content.video.source.embed, uid);


	if (content.image.image && /(jpe?g|png|svg|gif)$/.test(content.image.image.url.toLowerCase())) {
		const responsiveImages = imageSizeTranslation(columnWidth, content.image.image, (first ? 'eager' : 1), keepColumnsMobile);
		const img = <LazyImage {...responsiveImages} className={[s.mediaInner_image, (sectionHeight[1] ? s.mediaInner_image__fill : null), s[`mediaInner_image__${content.image.origin.horizontal}`], s[`mediaInner_image__${content.image.origin.vertical}`]]} alt={content.image.image.alt} />;

		// Is video or embed defined add dynamic loading of film to wrapper or just return image
		if (content?.video?.source?.video) {
			return (
				<VideoWrapper src={content.video.source.video} sectionHeight={sectionHeight} options={content.video?.controls}>
					{ img }
				</VideoWrapper>
			);
		}
		if (embedSrc) {
			return (
				<EmbedWrapper src={embedSrc} uid={uid} img={img} sectionHeight={sectionHeight} options={content.video?.controls} settings={content.video?.settings} />
			);
		}
		return img;
	}

	if (content?.video?.source?.video && /mp4$/.test(content.video.source.video.url.toLowerCase())) {
		// Video using html video tag - no image
		return <VideoWrapper src={content.video.source.video} sectionHeight={sectionHeight} options={content.video?.controls} enableEvents={!content.link || !alreadyHasLink} onInit />;
	}

	if (embedSrc) {
		return (
			<EmbedWrapper src={embedSrc} uid={uid} sectionHeight={sectionHeight} options={content.video?.controls} settings={content.video?.settings} enableEvents={!content.link || !alreadyHasLink} onInit>
				<div id={`iframe-${embedSrc}-${uid}`} />
			</EmbedWrapper>
		);
	}

	return null;
};

const MediaInner = ({ content, columnWidth, setup, alreadyHasLink = false, ...props }) => (
	<div className={[s.mediaInner, (content.textDetails.overlayOff ? null : s.mediaInner__overlay), (content.textDetails.overlayOff && content.textDetails.origin.vertical === 'top' ? s.mediaInner__captionTop : null )].join(' ')}>
		{ content.image.image || content?.video?.source?.video || content?.video?.source?.embed ? <MediaContent columnWidth={columnWidth} content={content} {...props} /> : null }
		<div className={s.mediaInner_caption}><MediaCaption content={content} setup={setup} {...props} onTop /></div>
		<MediaCaptionLink content={content} setup={setup} alreadyHasLink={alreadyHasLink} />
	</div>
);

const Media = ({ content, setup, ...props }) => {
	if (!content) return null;
	if (!content?.image?.image && !content?.video?.source?.video && !content?.video?.source?.embed) return null;
	const localizedLink = content.link ? cleanInternalLinks(translate(setup, content.link, 'url')) : false;

	if (localizedLink && /^(tel|mailto|https?)/.test(localizedLink)) {
		return (
			<a className={s.media} href={localizedLink} rel="noopener noreferrer" target="_blank">
				<MediaInner content={content} setup={setup} {...props} alreadyHasLink />
			</a>
		);
	}

	if (localizedLink) {
		return (
			<Link className={s.media} to={prefix(setup, localizedLink)}>
				<MediaInner content={content} setup={setup} {...props} alreadyHasLink />
			</Link>
		);
	}

	return (
		<div className={s.media}>
			<MediaInner content={content} setup={setup} {...props} />
		</div>
	);
};

export default hot(module)(Media);
